import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {action, observable, makeObservable} from 'mobx';
import {observer} from 'mobx-react';

//lodash
import debounce from 'lodash/debounce';
import find from 'lodash/find';

//components
import Icon from 'components/Icon';

//styled-components
import {ResizingMessage, StepsSwiperResizeListener} from './styles';

//constants
const EXTENDED_PORTRAIT_RATIO = 4 / 7;
const LANDSCAPE_RATIO = 8 / 3;
const PORTRAIT_RATIO = 2 / 3;

@observer
export default class StepsSwiperResizeListenerComponent extends Component {
  static defaultProps = {
    disabled: false,
    eventName: 'resize',
    minWidth: 200,
    navigationArrowsHeight: 0,
    shouldHideContentWhileResizing: true
  };

  static propTypes = {
    defaultResponsiveMargin: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    // The custom "orientationchangeend" event requires and emulator to be
    // installed. Reference: clients/viewer-mobile/config/polyfills.js.
    eventName: PropTypes.oneOf(['orientationchangeend', 'resize']),
    maxCardWidth: PropTypes.number.isRequired,
    minHeight: PropTypes.number.isRequired,
    minPortraitRatio: PropTypes.number.isRequired,
    minWidth: PropTypes.number,
    navigationArrowsHeight: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    responsiveMargins: PropTypes.objectOf(
      PropTypes.shape({
        breakpoint: PropTypes.number.isRequired,
        cardHint: PropTypes.number.isRequired,
        cardSpacing: PropTypes.number.isRequired
      })
    ).isRequired,
    shouldHideContentWhileResizing: PropTypes.bool
  };

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  @observable width = 0;
  @observable height = 0;
  @observable isResizing = true;

  eventName = this.props.eventName; // to keep it constant
  wrapperRef = null;

  componentDidMount() {
    window.addEventListener(this.eventName, this.handleWindowResize);
    this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener(this.eventName, this.handleWindowResize);
  }

  render() {
    return <StepsSwiperResizeListener ref={this.setWrapperRef}>{this.renderChildren()}</StepsSwiperResizeListener>;
  }

  renderChildren() {
    const {children, minHeight, minWidth, shouldHideContentWhileResizing} = this.props;

    if (this.isResizing && shouldHideContentWhileResizing) {
      return (
        <ResizingMessage>
          <Icon name="resize" />
        </ResizingMessage>
      );
    }

    if (this.width < minWidth || this.height < minHeight) {
      return (
        <ResizingMessage>
          <Icon name="resize-problem" />
        </ResizingMessage>
      );
    }

    return children;
  }

  @action
  handleWindowResize = () => {
    const {disabled} = this.props;

    if (disabled || !this.wrapperRef) {
      return;
    }

    const {width, height} = this.wrapperRef.getBoundingClientRect();
    const isFullScreenVideo = width === window.innerWidth;
    // First check if changed, otherwise fullscreen video's freak won't work
    if (!isFullScreenVideo && (this.width !== width || this.height !== height)) {
      this.isResizing = true;
      this.debounce(() => {
        this.updateDimensions();
      });
    }
  };

  @action
  updateDimensions = () => {
    if (!this.wrapperRef) {
      return;
    }

    const {width, height} = this.wrapperRef.getBoundingClientRect();

    this.width = width;
    this.height = height;
    this.isResizing = false;

    this.calculateLayout({width, height});
  };

  calculateLayout = ({width, height}) => {
    const {maxCardWidth, minPortraitRatio, navigationArrowsHeight, onChange} = this.props;

    const {cardHint, cardSpacing} = this.getResponsiveMargins();

    const isPortrait = width / height < minPortraitRatio;
    const availableWidth = Math.min(width - 2 * (cardHint + cardSpacing), maxCardWidth);
    const availableHeight = height - navigationArrowsHeight;

    const {imageHeight, imageWidth, slideHeight, slideWidth} = this[
      isPortrait ? 'getPortraitSizes' : 'getLandscapeSizes'
    ](availableWidth, availableHeight, cardSpacing);

    const extraVerticalSpace = (availableHeight - slideHeight) / 2;
    const orientation = isPortrait ? 'portrait' : 'landscape';
    const overlayHeight = availableHeight;
    const overlayWidth = (width - slideWidth) / 2;

    const containerHeight = availableHeight - extraVerticalSpace;
    const ratio = isPortrait ? PORTRAIT_RATIO : LANDSCAPE_RATIO;

    onChange({
      cardHint,
      cardSpacing,
      containerHeight,
      extraVerticalSpace,
      imageHeight,
      imageWidth,
      orientation,
      overlayHeight,
      overlayWidth,
      slideHeight,
      slideWidth,
      ratio
    });
  };

  debounce = debounce(callback => callback(), 250);

  getLandscapeSizes = (availableWidth, availableHeight, cardSpacing) => {
    let cardWidth, cardHeight;

    if (availableHeight * LANDSCAPE_RATIO > availableWidth) {
      cardWidth = availableWidth;
      cardHeight = availableWidth / LANDSCAPE_RATIO;
    } else {
      cardWidth = availableHeight * LANDSCAPE_RATIO;
      cardHeight = availableHeight;
    }

    return {
      imageHeight: cardHeight,
      imageWidth: cardWidth / 2,
      slideHeight: cardHeight,
      slideWidth: cardWidth + cardSpacing
    };
  };

  getPortraitSizes = (availableWidth, availableHeight, cardSpacing) => {
    let cardWidth, cardHeight;

    if (availableHeight * PORTRAIT_RATIO > availableWidth) {
      cardWidth = availableWidth;
      cardHeight = availableWidth / PORTRAIT_RATIO;

      if (cardHeight < availableHeight) {
        cardHeight = Math.min(cardWidth / EXTENDED_PORTRAIT_RATIO, availableHeight);
      }
    } else {
      cardWidth = availableHeight * PORTRAIT_RATIO;
      cardHeight = availableHeight;
    }

    return {
      imageHeight: (cardWidth * 3) / 4,
      imageWidth: cardWidth,
      slideHeight: cardHeight,
      slideWidth: cardWidth + cardSpacing
    };
  };

  getResponsiveMargins = () => {
    const {defaultResponsiveMargin, responsiveMargins} = this.props;

    return (
      find(responsiveMargins, margins => margins.breakpoint > this.width) || responsiveMargins[defaultResponsiveMargin]
    );
  };

  setWrapperRef = node => {
    this.wrapperRef = node;
  };
}
